import { ChevronDown, Clock, Eye, EyeOff, Pin } from 'lucide-react';
import { useState } from 'react';

import {
    type BackgroundWorkRow,
    type WatchRow,
} from '@apps/web/features/phoebe_chat_v3/model/background_work';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';

const TRAY_TITLE = 'What Phoebe is working on';
// Two pending check-ins plus five active watches can be live at once. Older
// suspended watches stay counted without pushing the composer off screen.
const MAX_VISIBLE_ROWS = 7;
const TRAY_SHADOW_CLASS =
    'shadow-[0_1px_2px_rgba(0,36,41,0.04),0_4px_14px_rgba(0,36,41,0.05)]';

function formatCadence(minutes: number): string {
    if (minutes >= 60 && minutes % 60 === 0) {
        const hours = minutes / 60;
        return hours === 1 ? 'Every hour' : `Every ${hours} hr`;
    }
    return `Every ${minutes} min`;
}

function watchDetail(row: WatchRow, timezone: string): string {
    return row.suspended
        ? row.reason
        : `${row.reason} · until ${formatDateAndTime(row.expiresAt, timezone)}`;
}

function TrayRow({
    row,
    timezone,
}: {
    readonly row: BackgroundWorkRow;
    readonly timezone: string;
}) {
    const isCheckIn = row.kind === 'check_in';
    const Icon = isCheckIn ? Clock : row.suspended ? EyeOff : Eye;
    const label = isCheckIn
        ? 'Check-in'
        : row.suspended
          ? 'Monitoring paused'
          : 'Monitoring';
    const detail = isCheckIn ? row.reason : watchDetail(row, timezone);
    const trailing = isCheckIn
        ? formatDateAndTime(row.visibleAt, timezone)
        : formatCadence(row.checkEveryMinutes);

    return (
        <li className="border-border/50 flex items-center gap-2.5 border-t px-4 py-[9px] text-sm">
            <span className="flex w-4 shrink-0 justify-center">
                <Icon className="text-muted-foreground h-4 w-4" />
            </span>
            <span className="text-foreground min-w-0 flex-1 truncate">
                <span className="font-medium">{label}</span>
                {detail !== null ? (
                    <span className="text-muted-foreground"> · {detail}</span>
                ) : null}
            </span>
            <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">
                {trailing}
            </span>
        </li>
    );
}

// Display-only by design: cancelling a check-in or watch goes through the
// agent (cancel_poke / cancel_watch), which keeps the agent's own view of its
// schedule truthful. A one-click cancel here would silently invalidate it.
export function BackgroundWorkTray({
    rows,
    timezone,
}: {
    readonly rows: ReadonlyArray<BackgroundWorkRow>;
    readonly timezone: string;
}) {
    const [collapsed, setCollapsed] = useState(false);

    if (rows.length === 0) {
        return null;
    }

    const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);
    const hiddenRowCount = rows.length - visibleRows.length;

    if (collapsed) {
        return (
            <button
                type="button"
                aria-label={`Expand background work, ${rows.length} items`}
                aria-expanded={false}
                onClick={() => setCollapsed(false)}
                className={cn(
                    'border-border bg-muted/40 text-foreground hover:bg-muted/60 focus-visible:ring-ring mb-2 inline-flex h-[34px] max-w-full items-center gap-2 rounded-full border px-3 text-[13px] focus-visible:ring-2 focus-visible:outline-none',
                    TRAY_SHADOW_CLASS,
                )}
            >
                <Pin className="text-primary h-3 w-3 shrink-0" />
                <span className="truncate font-medium">
                    Working on {rows.length}
                </span>
            </button>
        );
    }

    return (
        <section
            aria-label={TRAY_TITLE}
            className={cn(
                'border-border bg-muted/40 mb-2 w-full overflow-hidden rounded-[14px] border',
                TRAY_SHADOW_CLASS,
            )}
        >
            <div className="flex items-center gap-2 px-4 pt-3 pb-2.5">
                <Pin className="text-primary h-3.5 w-3.5 shrink-0" />
                <span className="text-foreground min-w-0 truncate text-[13px] font-semibold">
                    {TRAY_TITLE}
                </span>
                <span className="text-muted-foreground shrink-0 text-[13px]">
                    · {rows.length}
                </span>
                <button
                    type="button"
                    aria-expanded={true}
                    onClick={() => setCollapsed(true)}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring ml-auto inline-flex shrink-0 items-center gap-1 rounded-sm text-xs focus-visible:ring-2 focus-visible:outline-none"
                >
                    Collapse
                    <ChevronDown className="h-3 w-3" />
                </button>
            </div>
            <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                    <TrayRow
                        key={`${row.kind}:${row.id}`}
                        row={row}
                        timezone={timezone}
                    />
                ))}
            </ul>
            {hiddenRowCount > 0 ? (
                <div className="border-border/50 text-muted-foreground border-t px-4 py-[9px] text-sm">
                    +{hiddenRowCount} more background{' '}
                    {hiddenRowCount === 1 ? 'item' : 'items'}
                </div>
            ) : null}
        </section>
    );
}
